import api from '../api/api';
import {
  GET_MASTER_SLAVE_SUCCESS,
  RESET_STORE,
  SELECT_MASTER_SLAVE_SUCCESS,
  SET_CLIENT_TOKEN,
  SET_IS_GLOBAL_MODE
} from './const';
import { fetchUser } from 'store/slices/userSlice/userActions';
import { setLoading } from 'store/slices/userSlice';
import { isNewBackend, isRenderedAsIframeInNewUI } from 'config';
import { getSitesPerUser } from '../../../../pages/alertPage/controllers/alertsActions';
import { isSiteTypeSitesContainer } from 'common/Helpers/Sites/SitesHelper';
import { postIframeMessage } from 'utils/postPerentIframeMessage';
import { SESSION_LOGGING_MESSAGE_KEY } from 'pages/SiteLoadingPage/constants';

export function getMasterSlaves(shouldGetUser = false) {
  return async function (dispatch) {
    if (isRenderedAsIframeInNewUI) {
      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'getMasterSlaves:start',
        details: { shouldGetUser }
      });
    }
    try {
      let masterSlave = await api.getMasterSlaves();

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_SSPM_URL) {
        masterSlave = { ...masterSlave, TenantAccessKey: 'dev' };
      }
      dispatch({ type: GET_MASTER_SLAVE_SUCCESS, masterSlave });

      if (shouldGetUser) {
        await dispatch(fetchUser());
      }

      if (isRenderedAsIframeInNewUI) {
        postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
          step: 'getMasterSlaves:success',
          details: {
            shouldGetUser,
            CurrentSiteGlobalGuid: masterSlave?.CurrentSiteGlobalGuid,
            CurrentSiteGuid: masterSlave?.CurrentSiteGuid
          }
        });
      }
    } catch (error) {
      if (isRenderedAsIframeInNewUI) {
        postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
          type: 'error',
          step: 'getMasterSlaves:error',
          details: {
            error: JSON.stringify(error),
            shouldGetUser,
            message: 'getMasterSlaves request failed'
          }
        });
      }
      console.warn(error);
      throw error;
    }
  };
}

export function getClientToken(CurrentyLoggedClient) {
  return function (dispatch) {
    if (CurrentyLoggedClient) {
      return api.getClientToken().then(res => {
        let matched = res.match(/client_token='([^']+)'/);
        let clientTokenRes = matched && matched[1];
        dispatch({ type: SET_CLIENT_TOKEN, clientToken: clientTokenRes || null });
      });
    } else {
      dispatch({ type: SET_CLIENT_TOKEN, clientToken: null });
    }
  };
}

export function selectMasterSlave(
  isMaster,
  slave,
  isGlobal,
  redirectCallback = () => {},
  skipSelectMasterSlave = false
) {
  return async (dispatch, getState) => {
    const state = getState().masterSlave;
    let selectedSlave;
    if (isNewBackend) {
      const masterSlaveObj = state.masterSlave;
      selectedSlave = masterSlaveObj?.SlaveDetails?.find(x => x.DbTenantId === masterSlaveObj.CurrentSiteGuid);
    }
    try {
      dispatch({ type: RESET_STORE });
      dispatch(setLoading(true));
      if (!skipSelectMasterSlave) {
        const res = await api.selectMasterSlave(isMaster, slave, isGlobal);
        if (res.redirect) {
          //since we are redirecting, we need to wait for the redirect to happen
          await new Promise(resolve => setTimeout(resolve, 10000));
          throw new Error('Redirect to the selected site timed out after 10 seconds');
        }
        const masterSlave = Object.assign({}, state.masterSlave, {
          IsMaster: !isMaster //I was master, now I am not or vice-versa
        });
        dispatch({ type: SELECT_MASTER_SLAVE_SUCCESS, masterSlave });
      }
      await dispatch(getMasterSlaves(true));
      redirectCallback?.();
      dispatch(setLoading(false));
      // when switching from siteContainer to siteContainer (possible only in new backend), we need to get the sites per user
      // because the sites per user call calls when global alerts page is loaded (not loaded when switching from siteContainer to siteContainer)
      const isSelectedAndCurrentSitesAreSiteContainer =
        isSiteTypeSitesContainer(selectedSlave?.SiteType) && isSiteTypeSitesContainer(slave?.SiteType);
      const isCurrentEqualAllAndSelectedIsSiteContainer = isSiteTypeSitesContainer(slave?.SiteType) && !selectedSlave;
      const isCurrentSiteContainerAndSelectedIsAll = isMaster && isSiteTypeSitesContainer(selectedSlave?.SiteType);
      if (
        isNewBackend &&
        (isSelectedAndCurrentSitesAreSiteContainer ||
          isCurrentEqualAllAndSelectedIsSiteContainer ||
          isCurrentSiteContainerAndSelectedIsAll)
      ) {
        await dispatch(getSitesPerUser());
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
}

const redirectAwaitTime = 10000;

export function selectMasterSlaveIframe(isMaster, slave, isGlobal, skipSelectMasterSlave = false) {
  return async (dispatch, getState) => {
    postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
      step: 'useIframeSiteSelection:selectMasterSlaveIframe:start'
    });

    const state = getState().masterSlave;
    let selectedSlave;

    if (isNewBackend) {
      const masterSlaveObj = state.masterSlave;
      selectedSlave = masterSlaveObj?.SlaveDetails?.find(x => x.DbTenantId === masterSlaveObj.CurrentSiteGuid);
    }
    try {
      dispatch({ type: RESET_STORE });
      dispatch(setLoading(true));
      if (!skipSelectMasterSlave) {
        const res = await api.selectMasterSlave(isMaster, slave, isGlobal);

        postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
          step: 'useIframeSiteSelection:selectMasterSlaveIframe:api.selectMasterSlave',
          details: { hasRedirect: Boolean(res.redirect) }
        });

        if (res.redirect) {
          //since we are redirecting, we need to wait for the redirect to happen
          await new Promise(resolve => setTimeout(resolve, redirectAwaitTime));
          throw new Error('Redirect to the selected site timed out after 10 seconds');
        }
        const masterSlave = Object.assign({}, state.masterSlave, {
          IsMaster: !isMaster //I was master, now I am not or vice-versa
        });
        dispatch({ type: SELECT_MASTER_SLAVE_SUCCESS, masterSlave });
      }
      await dispatch(getMasterSlaves(true));
      dispatch(setLoading(false));
      // when switching from siteContainer to siteContainer (possible only in new backend), we need to get the sites per user
      // because the sites per user call calls when global alerts page is loaded (not loaded when switching from siteContainer to siteContainer)
      const isSelectedAndCurrentSitesAreSiteContainer =
        isSiteTypeSitesContainer(selectedSlave?.SiteType) && isSiteTypeSitesContainer(slave?.SiteType);
      const isCurrentEqualAllAndSelectedIsSiteContainer = isSiteTypeSitesContainer(slave?.SiteType) && !selectedSlave;
      const isCurrentSiteContainerAndSelectedIsAll = isMaster && isSiteTypeSitesContainer(selectedSlave?.SiteType);
      if (
        isNewBackend &&
        (isSelectedAndCurrentSitesAreSiteContainer ||
          isCurrentEqualAllAndSelectedIsSiteContainer ||
          isCurrentSiteContainerAndSelectedIsAll)
      ) {
        await dispatch(getSitesPerUser());
      }

      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useIframeSiteSelection:selectMasterSlaveIframe:finish'
      });
    } catch (error) {
      console.error(error);

      postIframeMessage(SESSION_LOGGING_MESSAGE_KEY, {
        step: 'useIframeSiteSelection:selectMasterSlaveIframe:ERROR_BLOCK',
        details: {
          error: JSON.stringify(error)
        }
      });

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
}

export function isGlobalMode(isMaster, slave) {
  return function (dispatch, getState) {
    return api
      .getIsGlobalMode()
      .then(result => {
        dispatch({ type: SET_IS_GLOBAL_MODE, result });
        return result;
      })
      .catch(error => {
        console.warn(error);
        throw error;
      });
  };
}

export function updateMasterSlaveForWhiteLabeledSites(sites) {
  return (dispatch, getState) => {
    const state = getState().masterSlave;
    if (!state?.masterSlave) return;

    const CurrentSiteGuid = state.masterSlave.CurrentSiteGuid;
    const whoAmiSite = sites.find(site => site.DbTenantId === CurrentSiteGuid);
    if (!whoAmiSite) return;

    dispatch({
      type: GET_MASTER_SLAVE_SUCCESS,
      masterSlave: {
        ...state.masterSlave,
        isCurrentSiteWhiteLabeled: whoAmiSite?.isWhiteLabeled ?? false,
        currentAccountGuid: whoAmiSite?.accountGuid,
        cdnBaseUrl: whoAmiSite?.accountCdnBaseUrl
      }
    });
  };
}

export function updateMasterSlaveFromHowAmI(whoAmiSite) {
  return (dispatch, getState) => {
    const state = getState().masterSlave;
    if (!state?.masterSlave) return;

    dispatch({
      type: GET_MASTER_SLAVE_SUCCESS,
      masterSlave: {
        ...state.masterSlave,
        isCurrentSiteWhiteLabeled: whoAmiSite?.isCurrentSiteWhiteLabeled ?? false,
        currentAccountGuid: whoAmiSite?.currentAccountGuid,
        cdnBaseUrl: whoAmiSite?.cdnBaseUrl
      }
    });
  };
}
